import { useEffect, useRef, useState } from "react";
import { Monitor, Maximize2, ExternalLink, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ScreenMedia, { PHASES } from "@/components/ScreenMedia";

const PHASE_TEXT = {
  idle: ["Gioca e vinci!", "Inserisci il codice o scansiona il QR"],
  playing: ["Estrazione in corso…", "Incrociamo le dita"],
  win: ["HAI VINTO!", "Ritira il premio in cassa"],
  lose: ["Non hai vinto", "Ritenta con il prossimo acquisto"],
};

export function ScreenPreviewDialog({ contestId, game, format, label, ratio }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [auto, setAuto] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open || !auto) return undefined;
    const keys = PHASES.map(([k]) => k);
    const t = setInterval(() => setPhase((p) => keys[(keys.indexOf(p) + 1) % keys.length]), 3000);
    return () => clearInterval(t);
  }, [open, auto]);
  const [title, subtitle] = PHASE_TEXT[phase];
  const fullscreen = () => box.current?.requestFullscreen?.();
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="mt-1 inline-flex items-center gap-1 text-[10px] text-primary hover:underline" data-testid={`screen-preview-${format}`}>
        <Monitor className="h-3 w-3" /> Anteprima monitor
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl" data-testid="screen-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Monitor className="h-4 w-4" /> Anteprima · {label}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-1.5">
            {PHASES.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPhase(k)} data-testid={`screen-preview-phase-${k}`}
                className={`text-xs rounded-full px-3 py-1 border ${phase === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{l}</button>
            ))}
            <span className="flex-1" />
            <label className="flex items-center gap-1.5 text-xs cursor-pointer mr-1" data-testid="screen-preview-auto-label">
              <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="screen-preview-auto" />
              <Play className="h-3 w-3" /> Ciclo automatico (3 s)
            </label>
            <Button size="sm" variant="secondary" className="gap-1.5 h-8" onClick={fullscreen} data-testid="screen-preview-fullscreen"><Maximize2 className="h-3.5 w-3.5" /> Schermo intero</Button>
            <a href={`/display/${contestId}?format=${format}`} target="_blank" rel="noreferrer" data-testid="screen-preview-open-display">
              <Button size="sm" variant="outline" className="gap-1.5 h-8 border-border"><ExternalLink className="h-3.5 w-3.5" /> Apri display (salvato)</Button>
            </a>
          </div>
          <div className="w-full grid place-items-center bg-black/60 rounded-xl p-3">
            <div ref={box} style={{ aspectRatio: ratio, background: game.bg_color || "#0f172a", maxHeight: "60vh", maxWidth: "100%", width: ratio >= 1 ? "100%" : "auto", height: ratio < 1 ? "60vh" : "auto" }}
              className="relative overflow-hidden rounded-lg shadow-2xl text-white grid place-items-center" data-testid="screen-preview-box">
              <ScreenMedia key={phase} screens={game.screens} phase={phase} format={format} fit={game.screens_fit || "cover"} />
              <div className="relative z-10 text-center px-6 drop-shadow-[0_2px_8px_rgba(0,0,0,.8)] animate-in fade-in duration-500" key={`t-${phase}`}>
                <p className="font-display font-extrabold" style={{ fontSize: "clamp(18px, 6vmin, 64px)" }}>{title}</p>
                <p className="opacity-90" style={{ fontSize: "clamp(11px, 2.4vmin, 24px)" }}>{subtitle}</p>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Mostra la grafica attualmente nell'editor (anche non salvata), con le proporzioni esatte del formato. "Schermo intero" usa tutto il monitor di questo dispositivo.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
